import React, { createContext, useContext } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import "./MarkdownContent.scss";

interface Props {
  content: string;
  /** Renders a markdown image; the default is a plain <img>. */
  renderImage?: (src: string, alt: string) => React.ReactNode;
}

type RenderImage = (src: string, alt: string) => React.ReactNode;

const RenderImageContext = createContext<RenderImage | undefined>(undefined);

function MarkdownImage({ src, alt }: { src?: string; alt?: string }) {
  const renderImage = useContext(RenderImageContext);
  if (renderImage && src) return <>{renderImage(src, alt ?? "")}</>;
  return <img src={src} alt={alt} />;
}

// ReactMarkdown uses these functions as component types, so they must keep
// their identity across renders: a new function remounts everything under it,
// and a remounted image starts loading again.
const components = {
  code({ node: _node, inline, className, children, ...props }: { node?: unknown; inline?: boolean; className?: string; children?: React.ReactNode; [key: string]: unknown }) {
    const match = /language-(\w+)/.exec(className || "");
    const language = match ? match[1] : "";

    return !inline ? (
      <div className="code-block-wrapper">
        {language && (
          <div className="code-block-header">
            <span className="code-block-language">{language}</span>
          </div>
        )}
        <SyntaxHighlighter
          // react-syntax-highlighter's prism style export and the
          // SyntaxHighlighter style prop disagree on type; a known friction.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          style={vscDarkPlus as any}
          language={language || "text"}
          PreTag="div"
          customStyle={{
            margin: 0,
            borderRadius: language ? "0 0 8px 8px" : "8px",
          }}
          {...props}
        >
          {String(children).replace(/\n$/, "")}
        </SyntaxHighlighter>
      </div>
    ) : (
      <code className="inline-code" {...props}>
        {children}
      </code>
    );
  },
  pre({ children }: { children?: React.ReactNode }) {
    return <>{children}</>;
  },
  table({ children }: { children?: React.ReactNode }) {
    return (
      <div className="markdown-table-wrapper">
        <table>{children}</table>
      </div>
    );
  },
  blockquote({ children }: { children?: React.ReactNode }) {
    return <blockquote className="markdown-blockquote">{children}</blockquote>;
  },
  img: MarkdownImage,
  a({ href, children }: { href?: string; children?: React.ReactNode }) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
};

const MarkdownContent: React.FC<Props> = ({ content, renderImage }) => {
  return (
    <div className="markdown-content">
      <RenderImageContext.Provider value={renderImage}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {content}
        </ReactMarkdown>
      </RenderImageContext.Provider>
    </div>
  );
};

export default MarkdownContent;
